import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { TrendingUp, Users, Activity, Repeat, Banknote, Megaphone, Magnet } from "lucide-react";

const Stat = ({ label, value, sub }) => (
  <div className="rounded-xl border border-border bg-background p-4">
    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
    <div className="mt-1 font-display text-2xl">{value}</div>
    {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

const Section = ({ icon: Icon, title, children }) => (
  <div className="rounded-2xl border border-border bg-card p-5">
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-primary" />
      <h3 className="font-display text-base">{title}</h3>
    </div>
    <div className="mt-4">{children}</div>
  </div>
);

export const AdminInsights = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics/insights")
      .then(({ data }) => setData(data))
      .catch(() => setError("Could not load insights"));
  }, []);

  if (error) return <p className="py-8 text-sm text-muted-foreground">{error}</p>;
  if (!data) return <p className="py-8 text-sm text-muted-foreground">Crunching the numbers…</p>;

  const { growth, engagement, retention, features, referrals, revenue, acquisition, leads } = data;
  const ret = (r) => (r.pct === null ? "n/a" : `${r.pct}% (${r.kept}/${r.eligible})`);

  return (
    <div className="space-y-4" data-testid="admin-insights">
      {/* Growth */}
      <Section icon={TrendingUp} title="Growth">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label="Total users" value={growth.total_users} />
          <Stat label="New users (7 days)" value={growth.new_7d} />
          <Stat label="New users (30 days)" value={growth.new_30d} />
          <Stat label="MoM growth" value={growth.mom_pct === null ? "n/a" : `${growth.mom_pct > 0 ? "+" : ""}${growth.mom_pct}%`} sub={`prev 30d: ${growth.prev_30d}`} />
        </div>
        <div className="mt-4 h-44">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={growth.daily_signups}>
              <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} stroke="currentColor" opacity={0.5} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} stroke="currentColor" opacity={0.5} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
              <Bar dataKey="signups" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-1 text-[10px] text-muted-foreground">Daily signups — last 30 days</p>
      </Section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Engagement */}
        <Section icon={Activity} title="Engagement">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="DAU" value={engagement.dau} />
            <Stat label="WAU" value={engagement.wau} />
            <Stat label="MAU" value={engagement.mau} />
            <Stat label="Stickiness" value={`${engagement.stickiness_pct}%`} sub="DAU / MAU" />
          </div>
        </Section>

        {/* Retention */}
        <Section icon={Repeat} title="Retention (cohort)">
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Day 1" value={ret(retention.d1)} />
            <Stat label="Day 7" value={ret(retention.d7)} />
            <Stat label="Day 30" value={ret(retention.d30)} />
          </div>
          <p className="mt-2 text-[10px] text-muted-foreground">% of users with any activity N+ days after signing up</p>
        </Section>

        {/* Revenue */}
        <Section icon={Banknote} title="Revenue">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Lifetime" value={`£${revenue.lifetime.toFixed(2)}`} sub={`${revenue.orders} orders`} />
            <Stat label="Last 30 days" value={`£${revenue.last_30d.toFixed(2)}`} />
            <Stat label="Unique payers" value={revenue.unique_payers} />
            <Stat label="Active premium" value={revenue.premium_active} />
          </div>
          {revenue.by_product.length > 0 && (
            <div className="mt-3 space-y-1">
              {revenue.by_product.map((p) => (
                <div key={p.product} className="flex justify-between rounded-lg bg-background px-3 py-1.5 text-xs">
                  <span>{p.product} <span className="text-muted-foreground">×{p.count}</span></span>
                  <span className="font-medium">£{p.revenue.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Acquisition */}
        <Section icon={Megaphone} title="Acquisition sources">
          <div className="space-y-1">
            {acquisition.map((a) => (
              <div key={a.source} className="flex items-center gap-2 text-xs">
                <span className="w-32 truncate">{a.source}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                  <div className="h-full rounded-full bg-accent" style={{ width: `${Math.max(2, a.pct)}%` }} />
                </div>
                <span className="w-16 text-right text-muted-foreground">{a.count} ({a.pct}%)</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[10px] text-muted-foreground">UTM/referrer captured from signups going forward (older accounts show as Direct)</p>
        </Section>

        {/* Features */}
        <Section icon={Users} title="Most used features">
          <div className="space-y-1">
            {features.slice(0, 10).map((f) => (
              <div key={f.label} className="flex justify-between rounded-lg bg-background px-3 py-1.5 text-xs">
                <span>{f.label}</span><span className="font-medium">{f.count}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* Funnels */}
        <Section icon={Magnet} title="Funnels">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Roadmap leads" value={leads.total} sub={`${leads.claimed} claimed (${leads.claim_rate_pct}%)`} />
            <Stat label="Referrals" value={referrals.successful} sub={`${referrals.referred_users} users referred (${referrals.conversion_pct}%)`} />
          </div>
        </Section>
      </div>
    </div>
  );
};
